
    // IMPORTS
    import { computed, defineComponent, onMounted, ref, watch } from 'vue'
    import { useRoute } from 'vue-router'
    import { NotificationDTO, useNotificationStore } from '@/store/notification'
    import { useUserStore } from '@/store/user'
    import { useBrowserStateStore } from '@/store/browserState'
    import { AccountApi, buildLocalization, DEBUG_ERR, performButtonAction } from '@/util'

    import { Card, CardHeader, CardTitle, CardBody } from '@progress/kendo-vue-layout'
    import { Grid, GridColumnProps } from '@progress/kendo-vue-grid'
    import LoaderGuard from '@/components/common/loader-guard.vue'
    import { Button } from '@progress/kendo-vue-buttons'
    import { IPermissionGrant, IUserAccountDTO } from '@/types/userAccount'
import { AxiosError } from 'axios'

    interface IUserRow extends IUserAccountDTO {
        isPartnerAdmin: boolean
        isPriceAccessAllowed: boolean
    }

    export default defineComponent({
        name: 'userAccount',

        components: {
            LoaderGuard,
            Card,
            CardHeader,
            CardTitle,
            CardBody,
            Grid,
            'k-button': Button
        },

        setup() {
            /********************************************************************************************************************
             INIT
             ********************************************************************************************************************/
            const userStore = useUserStore()
            const notificationStore = useNotificationStore()
            const browserState = useBrowserStateStore()

            /********************************************************************************************************************
             PRIVATE DATA
             ********************************************************************************************************************/
            const route = useRoute()

            // Grid rows
            const userData = ref<IUserRow[]>([])

            // Original data loaded from userStore
            const originalData = ref<Record<string, boolean>>({}) // key -> isPriceAccessAllowed

            // Dirty rows
            const dirtyRows = ref<Set<string>>(new Set())         // keys that differ from snapshot
            const keyField = 'email' as const
            type TKey = string

            /********************************************************************************************************************
             PUBLIC DATA
             ********************************************************************************************************************/
            const btnSave = ref<InstanceType<typeof Button> | null>(null)
            const publicData = { btnSave }

            /********************************************************************************************************************
             COMPUTED DATA
             ********************************************************************************************************************/
            const isNarrowScreen = computed(() => (browserState.resolution?.width ?? 1200) < 800)
            const userStoreData = computed(() => userStore.userData)
            const hasLoaded = computed(() => userStoreData.value != null)
            const userName = computed(() => userStoreData.value?.currentUser)
            const partnerAccount = computed(() => userStoreData.value?.partnerAccount)
            const partnerInfo = computed(() => partnerAccount.value != null
                ? `${partnerAccount.value.accountNo} ${partnerAccount.value.companyName}`
                : null
            )
            const currency = computed(() => partnerAccount.value != null
                ? partnerAccount.value.currency
                : 'EUR'
            )

            const anyChanges = computed(() => dirtyRows.value.size > 0)

            const showUserList = computed(() => route.name == 'partner-user-list')

            const hasPartnerAdmins = computed(() => userData.value != null && userData.value.some(user => user.permissions.isPartnerAdmin === true))

            const columns = computed(() => {
                let cols = [
                    {
                        field: 'email',
                        title: localize('Username'),
                        cell: 'general'
                    },
                    {
                        field: 'firstName',
                        title: localize('FirstName'),
                        cell: 'general'
                    },
                    {
                        field: 'lastName',
                        title: localize('LastName'),
                        cell: 'general'
                    },
                    {
                        field: 'isLockedOut',
                        title: localize('LockedOut'),
                        headerClassName: 'centered',
                        className: 'centered',
                        cell: 'bool'
                    }
                ] as GridColumnProps[]

                // Add column for "Partner admin" permission?
                if (hasPartnerAdmins.value) {
                    cols = [
                        ...cols,
                        {
                            field: 'isPriceAccessAllowed',
                            title: localize('IsPriceAccessAllowed'),
                            headerClassName: 'centered',
                            className: 'centered',
                            cell: 'isPriceAccessAllowed',
                        },
                        {
                            field: 'isPartnerAdmin',
                            title: localize('IsPartnerAdmin'),
                            headerClassName: 'centered',
                            className: 'centered',
                            cell: 'bool'
                        }
                    ]
                }

                if (isNarrowScreen.value) {
                    cols.forEach(col => col.width = 200)
                }

                return cols
            })

            const computedData = {
                isNarrowScreen,
                userData,
                hasLoaded,
                userName,
                partnerAccount,
                partnerInfo,
                currency,
                anyChanges,
                showUserList,
                hasPartnerAdmins,
                columns
            }

            /********************************************************************************************************************
             PUBLIC METHODS
             ********************************************************************************************************************/
            const { localize } = buildLocalization()

            function onCheckBoxChange(user: IUserRow, checked: boolean) {
                const key: TKey = user[keyField]
                user.isPriceAccessAllowed = checked

                // update dirty set against snapshot
                const wasChecked = originalData.value[key]
                if (wasChecked === checked) {
                    dirtyRows.value.delete(key)
                }
                else {
                    dirtyRows.value.add(key)
                }
            }

            async function saveChanges() {
                if (btnSave.value == null || !anyChanges.value) return

                const toSave = userData.value.filter(row => dirtyRows.value.has(row[keyField]))
                const permissionGrants: IPermissionGrant[] = toSave.map(row => ({
                    userEmail: row.email,
                    granted: row.isPriceAccessAllowed
                }))

                let success = true
                const api = new AccountApi
                const target = btnSave.value.$el as HTMLButtonElement
                const action = () => api.setUserPriceAccessState(permissionGrants)
                await performButtonAction(target, action)
                    .catch((error: AxiosError | Error) => {
                        DEBUG_ERR(error)
                        const message = localize('UnexpectedError')
                        notificationStore.addNotification(new NotificationDTO(message, 'error'))
                        success = false
                    })

                if (success) {
                    // Did current user change permissions? Then refresh user store to reflect changes
                    if (toSave.some(row => row.email === userStore.userData?.currentUser)) {
                        userStore.refresh()
                    }

                    // re-snapshot & clear dirty
                    RefreshSnapShotAndDirtyState()

                    const message = localize('Success_ChangesSaved')
                    notificationStore.addNotification(new NotificationDTO(message, 'success'))
                }
            }

            const publicMethods = { localize, onCheckBoxChange, saveChanges }

            /********************************************************************************************************************
             PRIVATE METHODS
             ********************************************************************************************************************/
            function RefreshSnapShotAndDirtyState() {
                const snapshot: Record<TKey, boolean> = {}
                for (const row of userData.value) {
                    snapshot[row[keyField]] = row.isPriceAccessAllowed
                }
                originalData.value = snapshot
                dirtyRows.value.clear()
            }

            /********************************************************************************************************************
             WATCHES
             ********************************************************************************************************************/
            // seed local data when store changes
            watch(
                () => userStore.userData?.partnerAccount?.userAccounts,
                (userList?: IUserAccountDTO[]) => {
                    const list = userList ?? []
                    userData.value = list.map(user => ({
                        ...user,
                        isPartnerAdmin: user.permissions.isPartnerAdmin,
                        isPriceAccessAllowed: user.permissions.priceAccessAllowed,
                    }))

                    // FOR TESTING: Add more users
                    //for (let i = 0; i < 100; i++) {
                    //    userData.value.push({
                    //        email: `user ${i}`,
                    //        firstName: 'TEST',
                    //        lastName: 'TEST',
                    //        permissions: {
                    //            isAdmin: false,
                    //            isPartnerAdmin: false,
                    //            priceAccessAllowed: false
                    //        },
                    //        isLockedOut: false,
                    //        isPartnerAdmin: false,
                    //        isPriceAccessAllowed: false
                    //    })
                    //}

                    // build snapshot and clear dirty
                    RefreshSnapShotAndDirtyState()
                },
                { immediate: true }
            )

            /********************************************************************************************************************
             WATCHES
             ********************************************************************************************************************/
            // always refresh user list, when accessing this page (due to lock out status)
            onMounted(() => userStore.refresh())

            /********************************************************************************************************************
             COMPONENT INTERFACE
             ********************************************************************************************************************/
            return { ...publicData, ...computedData, ...publicMethods }
        }
    });
